import { useEffect, useRef, useState } from "react"
import { useMediaPredicate } from "react-media-hook"
import {
  useAppContext,
  usePropsObject,
  usePathData
} from "@/lib/contexts/appContext"
import Block from "@/components/wrappers/block"
import Button from "@/common/button/button"
import Share from "@/common/social/share"
import styles from "./article-sidebars.module.css"
import { themes } from "@/common/article/cta.module.css"
import classNames from "classnames"

export default function Sidebars({ block }) {
  const [observeClass, setObserveClass] = useState("")
  const pathData = usePathData()
  const { mainCTA } = useAppContext()
  const object = usePropsObject()
  const sidebarRef = useRef()

  useEffect(() => {
    if (mainCTA.current) {
      const observer = new IntersectionObserver(
        (entries) => {
          const inView = entries[0]["isIntersecting"]
          const ratio = entries[0]["intersectionRatio"]

          if (inView && ratio === 1) {
            setObserveClass("not-visible")
          } else {
            setObserveClass("")
          }
        },
        { threshold: [0, 0.8, 1], rootMargin: "100% 0px 0px 0px" }
      )

      // TODO add core/image full/large here as well
      observer.observe(mainCTA.current)
    }
    return () => {
      // observer.disconnect() // FIXME this needs to have a cleanup method for the observer, somehow
    }
  }, [mainCTA])

  // allow for article-hero, article-sidebars and a single block
  const hasContent = pathData.content.length > 3
  if (!hasContent) {
    return null
  }

  return (
    <Block block={block} noStyling={true} className={styles.component}>
      {object.cta && object?.cta?.sticky && <FixedCTA cta={object.cta} />}
      <div className={styles.wrapper}>
        <Block
          className={classNames(styles.sidebar, observeClass)}
          blockRef={sidebarRef}>
          <div className="flex justify-between pointer-events-none">
            <div className="pointer-events-auto left bg">
              {object.cta && <CTA cta={object.cta} />}
            </div>
            <div className="pointer-events-auto right">
              <Share
                classes="w-icon-xl h-icon-xl mb-3"
                url={object.url}
                sharing={object.sharing}
                label="dela"
                wrapper="flex-col"
                iconSize="large"
              />
            </div>
          </div>
        </Block>
      </div>
    </Block>
  )
}

function CTA({
  isFloating = false,
  cta: { url, label, target, text, title, theme }
}) {
  const bgColor = theme?.bgcolor ? "theme-" + theme?.bgcolor : "bg-green-10"
  const txtColor = theme?.txtcolor === "white" ? "text-white" : null
  const outline = theme?.outline ? "outlined" : null

  return (
    <div
      className={classNames([
        styles.cta,
        themes,
        bgColor,
        txtColor,
        outline,
        {
          plate: !isFloating,
          floating: isFloating
        }
      ])}>
      <h2>{title}</h2>
      <div className="text" dangerouslySetInnerHTML={{ __html: text }} />
      {url && (
        <Button
          tagName="a"
          size={isFloating ? "mini" : "small"}
          href={url}
          theme={theme?.button ?? "green"}
          target={target}
          ariaLabel={label}
          className="inline-block no-underline">
          {label}
        </Button>
      )}
    </div>
  )
}

function FixedCTA({ cta }) {
  const [scrollY, setScrollY] = useState(0)
  const isMobile = useMediaPredicate("(max-width: 640px)")
  const [hasMounted, setMounted] = useState(false)
  const [isHidden, setHidden] = useState(false)
  const { mainElem } = useAppContext()

  const handleScroll = () => {
    setScrollY(window.scrollY)
    setHidden(
      mainElem.current.getBoundingClientRect().bottom < window.innerHeight
    )
  }

  useEffect(() => {
    let isMounted = true
    if (isMounted) {
      setMounted(true)
    }

    window.addEventListener("scroll", handleScroll, {
      passive: true
    })

    return () => {
      window.removeEventListener("scroll", handleScroll)
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainElem])

  if (!isMobile || !hasMounted || isHidden) {
    return null
  }

  return (
    <div
      className={classNames([
        styles["float-wrapper"],
        { hidden: scrollY < 100 }
      ])}>
      <CTA cta={cta} isFloating={true} />
    </div>
  )
}
